import { IconButton } from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { MessageBar } from '@fluentui/react/lib/MessageBar';
import { WindowProvider, useWindow } from '@fluentui/react/lib/WindowProvider';
import { Modal } from '@fluentui/react/lib/Modal';
import { observer } from 'mobx-react-lite';
import { ControlIcons } from 'owa-control-icons';
import type { FileProviderType } from 'owa-fileprovider-store';
import loc from 'owa-localize';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as filePickerActionsPanel from '../../actions/actionsPanelActions/showHideFilePickerActionsPanel';
import updateSaveUserChoice from '../../actions/actionsPanelActions/updateSaveUserChoice';
import { logSecondPanelUsage } from '../../actionsPanel';
import FilePickerAction from '../../store/schema/FilePickerAction';
import type FilePickerActionsPanelViewState from '../../store/schema/FilePickerActionsPanelViewState';
import { ComposeUniqueState } from '../../store/schema/FilePickerUploadConfiguration';
import { AttachOrSharePreference } from '../../store/schema/FilePickerUserConfiguration';
import type { SelectedFilesSource } from '../../types/FilePickerResult';
import { removeHTMLElementFromDOM } from '../FilePickerPanelView';
import doesComposeSupportReferenceAttachment from '../utils/doesComposeSupportReferenceAttachment';
import getShareIconForActionPanel from '../utils/getShareIconForActionPanel';
import getShareStringsForActionPanel, {
    ShareButtonStrings,
} from '../utils/getShareStringsForActionPanel';
import FilePickerActionsPanelButtonView from './FilePickerActionsPanelButtonView';
import FilePickerActionsPanelCheckboxView from './FilePickerActionsPanelCheckboxView';
import {
    ActionsPanel_AttachAsACopyDescriptionText,
    ActionsPanel_AttachAsACopyText,
    ActionsPanel_CloseButtonText,
    ActionsPanel_InsertPictureInlineText,
    ActionsPanel_InsertPictureInlineTextDescriptionText,
    ActionsPanel_MultipleFilesText,
    ActionsPanel_SingleFileText,
} from './FilePickerActionsPanelView.locstring.json';

interface FilePickerActionsPanelProps {
    onActionSelected: (action: FilePickerAction) => void;
    onCancelCallback: () => void;
    viewState: FilePickerActionsPanelViewState;
    supportedActions: FilePickerAction[];
    rememberUserChoiceText: string;
    userChoiceSetter: (value: AttachOrSharePreference) => void;
    isMultipleSelection: boolean;
    currentProviderDisplayName: string;
    currentProviderType: FileProviderType;
    supportedActionsMessages?: string[];
    filesSource: SelectedFilesSource;
    supportsInsertLink: boolean;
}

import styles from './FilePickerActionsPanelView.scss';

// This component is responsible for showing the share options for the user.
const FilePickerActionsPanelView = observer(function FilePickerActionsPanelView(
    props: FilePickerActionsPanelProps
) {
    const selectedAction = React.useRef<FilePickerAction>(null);
    const targetWindow = useWindow();
    const renderFilePickerActionsPanelContent = () => {
        const {
            supportedActions,
            isMultipleSelection,
            currentProviderDisplayName,
            viewState,
            filesSource,
            currentProviderType,
        } = props;
        const isGroupCalendar =
            viewState.uploadConfiguration &&
            viewState.uploadConfiguration.composeUniqueState === ComposeUniqueState.GroupCalendar;
        const isAttachSupported =
            supportedActions.some(action => action === FilePickerAction.Attach) && !isGroupCalendar;
        const isShareSupported =
            supportedActions.some(action => action === FilePickerAction.Share) &&
            (!viewState.uploadConfiguration ||
                doesComposeSupportReferenceAttachment(
                    viewState.uploadConfiguration.composeUniqueState
                ));
        const isInsertInlineImageSupported = supportedActions.some(
            action => action === FilePickerAction.InsertInlineImage
        );
        const shareButtonStrings: ShareButtonStrings = getShareStringsForActionPanel(
            filesSource,
            isMultipleSelection,
            currentProviderDisplayName,
            viewState.uploadConfiguration
        );
        const shareButtonIcon: JSX.Element = getShareIconForActionPanel(
            filesSource,
            currentProviderType,
            !isShareSupported
        );

        const attachButtonComponent = (
            <FilePickerActionsPanelButtonView
                action={FilePickerAction.Attach}
                actionLabel={loc(ActionsPanel_AttachAsACopyText)}
                actionDescription={loc(ActionsPanel_AttachAsACopyDescriptionText)}
                buttonIcon={<Icon iconName={ControlIcons.Attach} className={styles.icon} />}
                isDisabled={!isAttachSupported}
                onActionButtonClicked={onActionButtonClicked}
                shouldAutoFocus={isAttachSupported}
            />
        );
        const shareButtonComponent = (
            <FilePickerActionsPanelButtonView
                action={FilePickerAction.Share}
                actionLabel={shareButtonStrings.shareText}
                actionDescription={shareButtonStrings.shareDescription}
                buttonIcon={shareButtonIcon}
                isDisabled={!isShareSupported}
                onActionButtonClicked={onActionButtonClicked}
                shouldAutoFocus={!isAttachSupported && isShareSupported}
            />
        );

        const buttons = [shareButtonComponent, attachButtonComponent];

        return (
            <>
                <div className={styles.title}>
                    {isMultipleSelection
                        ? loc(ActionsPanel_MultipleFilesText)
                        : loc(ActionsPanel_SingleFileText)}
                </div>
                <div className={styles.closeButton}>
                    <IconButton
                        aria-label={loc(ActionsPanel_CloseButtonText)}
                        iconProps={{
                            iconName: ControlIcons.Cancel,
                        }}
                        onClick={closePanel}
                    />
                </div>
                {showActionsPanelMessages()}

                {buttons}

                {isInsertInlineImageSupported && (
                    <FilePickerActionsPanelButtonView
                        action={FilePickerAction.InsertInlineImage}
                        actionLabel={loc(ActionsPanel_InsertPictureInlineText)}
                        actionDescription={loc(ActionsPanel_InsertPictureInlineTextDescriptionText)}
                        buttonIcon={<Icon iconName={ControlIcons.Photo2} className={styles.icon} />}
                        isDisabled={!isAttachSupported}
                        onActionButtonClicked={onActionButtonClicked}
                        shouldAutoFocus={false}
                    />
                )}
                {props.rememberUserChoiceText && (
                    <FilePickerActionsPanelCheckboxView
                        viewState={props.viewState}
                        rememberUserChoiceText={props.rememberUserChoiceText}
                    />
                )}
            </>
        );
    };
    const showActionsPanelMessages = () => {
        return props.supportedActionsMessages.map((message, index) => {
            return (
                <MessageBar key={message} className={styles.errorMessage}>
                    {' '}
                    {message}{' '}
                </MessageBar>
            );
        });
    };
    const onActionButtonClicked = (action: FilePickerAction) => {
        // avoid logging the usage for uploading large files from local since that has been logged seperately
        // We only log when it is ODB, or, ODC from consumer account
        logSecondPanelUsage(props.currentProviderType, false, action);
        selectedAction.current = action;
        closePanel();
    };
    const closePanel = () => {
        filePickerActionsPanel.hide(props.viewState);
    };
    const onPanelDismissed = () => {
        // We need to make the callbacks only when the animation is complete so the focus can be changed back
        if (selectedAction.current !== null) {
            props.onActionSelected(selectedAction.current);
            if (props.viewState.saveUserChoice) {
                persistUsersPreferredAction(selectedAction.current);
                updateSaveUserChoice(props.viewState, false);
            }
            selectedAction.current = null;
        } else {
            props.onCancelCallback();
        }

        // Unmount the element and remove it from DOM
        const actionsPanelDiv = targetWindow.document.getElementById(ACTIONS_PANEL_DIV_ID);
        removeHTMLElementFromDOM(actionsPanelDiv);
    };
    const persistUsersPreferredAction = (action: FilePickerAction) => {
        if (action === FilePickerAction.Share) {
            props.userChoiceSetter(AttachOrSharePreference.Share);
        } else if (action === FilePickerAction.Attach) {
            props.userChoiceSetter(AttachOrSharePreference.Attach);
        }
    };
    const divRef = React.useRef<HTMLDivElement>();
    const focusActionsPanel = () => {
        divRef.current.tabIndex = 0;
        divRef.current.focus();
    };
    const focusOutActionsPanel = () => {
        divRef.current.tabIndex = -1;
    };
    return (
        <Modal
            isOpen={props.viewState.isOpen}
            onDismiss={closePanel}
            onDismissed={onPanelDismissed}
            forceFocusInsideTrap={true}>
            <div
                ref={divRef}
                className={styles.container}
                onClick={focusActionsPanel}
                onBlur={focusOutActionsPanel}>
                {renderFilePickerActionsPanelContent()}
            </div>
        </Modal>
    );
});

/**
 * The showFilePickerActionsPanel is responsible for creating an element and then rendering the FilePickerActionsPanel in it.
 * The reason to do that is to make this component simpler from a consumer perspective so that
 * they don't have to worry about creating the component themselves and maintaining show/close lifecycle.
 * Instead they create the panel and once the action is selected and the panel is dismissed they will get a callback
 * containing the user choice.
 */
const ACTIONS_PANEL_DIV_ID = 'ActionsPanelDiv';

export default function showFilePickerActionsPanel(
    resolve: (action: FilePickerAction) => void,
    reject: () => void,
    viewState: FilePickerActionsPanelViewState,
    supportedActions: FilePickerAction[],
    supportedActionsMessages: string[],
    rememberUserChoiceText: string,
    userChoiceSetter: (value: AttachOrSharePreference) => void,
    isMultipleSelection: boolean,
    currentProviderDisplayString: string,
    currentProviderType: FileProviderType,
    filesSource: SelectedFilesSource,
    targetWindow: Window,
    supportsInsertLink: boolean
): void {
    const document = (targetWindow || window).document;

    // With projection popout we can have several actions panel divs at the same time, so we must give them unique ids.
    const panelDiv = document.createElement('div');
    panelDiv.id = ACTIONS_PANEL_DIV_ID;
    document.body.appendChild(panelDiv);

    ReactDOM.render(
        <React.StrictMode>
            <WindowProvider window={targetWindow || window}>
                <FilePickerActionsPanelView
                    onActionSelected={resolve}
                    onCancelCallback={reject}
                    viewState={viewState}
                    supportedActions={supportedActions}
                    supportedActionsMessages={supportedActionsMessages}
                    rememberUserChoiceText={rememberUserChoiceText}
                    userChoiceSetter={userChoiceSetter}
                    isMultipleSelection={isMultipleSelection}
                    currentProviderDisplayName={currentProviderDisplayString}
                    currentProviderType={currentProviderType}
                    filesSource={filesSource}
                    supportsInsertLink={supportsInsertLink}
                />
            </WindowProvider>
        </React.StrictMode>,
        panelDiv
    );

    filePickerActionsPanel.show(viewState);
}
